import type { Item, Party, WhatsAppDocType } from '@erp/shared';
import Decimal from 'decimal.js';
import { ArrowLeft, Banknote, CheckCircle2, Gift, Pencil, Printer, Receipt, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/shadcn/index.js';
import { Badge, Button, Card, DirectionalIcon, Input, Skeleton, confirmDialog } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import { getCashbackBalance, redeemCashback } from '../../lib/cashback.js';
import { formatDate } from '../../lib/intl.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney, getCurrencyDecimals } from '../../lib/money.js';
import { WhatsAppButton } from '../common/WhatsAppButton.js';
import { ItemDetailDialog } from '../items/ItemDetailDialog.js';
import { SaleProfitCard } from '../reports/_shared/SaleProfitCard.js';

import { cashNotes, PAID_LABEL_DEFAULT, paidLabelKey } from './cashNotes.js';
import { partyPath } from './partyPath.js';
import { PostInvoiceDialog } from './PostInvoiceDialog.js';
import { reviseErrorMessage } from './reviseErrors.js';

export interface DocumentDetailPageProps {
  /** API path under /api/v1, e.g. "/invoices". */
  endpoint: string;
  /** Translation namespace key, e.g. "documents:invoices". */
  ns: string;
  /** Where to go on Back/cancel, e.g. "/sales/invoices". */
  listPath: string;
  /** If set, drives status badge color. */
  statusTone?: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral' | 'brand'>;
  /** If set, shows a Post button (gated on this permission) when status allows. */
  postPermission?: string;
  /** If set, shows an Edit link when status=draft, navigating to `${editPath}/${id}/edit`. */
  editPath?: string;
  /** Permission that gates the Edit link. */
  writePermission?: string;
  /** If set, shows a Print button linking to `${printPath}/${id}/print` (e.g. invoices). */
  printPath?: string;
  /** If set, shows a "Send via WhatsApp" button (when the customer has a phone). */
  whatsappDocType?: WhatsAppDocType;
  /** Override the due-date detail label key (e.g. 'detail.validUntil' for quotes). */
  /** Override for the FIRST date's label (bills call it the purchase date). */
  dateLabelKey?: string;
  dueDateLabelKey?: string;
  /** Enables the quotation workflow actions (send / accept / reject / convert). */
  quotation?: boolean;
}

/** "12.000000" -> "12". Pack factors are stored at full decimal scale. */
function trimNum(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : v;
}

interface DocLine {
  id: string;
  lineNo: number;
  itemId?: string | null;
  itemName?: string | null;
  itemCode?: string | null;
  unitName?: string | null;
  /** Base units per packaging unit — 1 for base-unit lines. */
  unitFactor?: string | null;
  /** The ITEM's own unit of measure (قطعة, دست…), for lines sold in the base unit. */
  itemUom?: string | null;
  description?: string | null;
  quantity: string;
  unitPrice: string;
  taxRate: string;
  taxAmount: string;
  lineTotal: string;
}

interface DocDetail {
  id: string;
  docNumber: string;
  docDate?: string;
  paymentDate?: string;
  dueDate?: string | null;
  partyId?: string;
  status: string;
  currencyCode: string;
  subtotal?: string;
  taxTotal?: string;
  discountTotal?: string;
  couponCode?: string | null;
  total?: string;
  amountPaid?: string;
  /** Invoices paid in another currency's notes (till / register) — see cashNotes.ts. */
  cashInCurrency?: string | null;
  cashInAmount?: string | null;
  cashBackCurrency?: string | null;
  cashBackAmount?: string | null;
  notes?: string | null;
  /** Purchase docs: where posting receives the goods. Null = tenant default. */
  warehouseId?: string | null;
  postedJournalEntryId?: string | null;
  /** Human-readable number of the posted entry, e.g. "JE-000123". */
  postedJournalEntryNumber?: string | null;
  /** True once that entry has a reversing entry against it. */
  postedJournalEntryReversed?: boolean;
  postedAt?: string | null;
  isCreditNote?: boolean | null;
  isDebitNote?: boolean | null;
  lines?: DocLine[];
  /** Sales orders: the invoice the order became, if any. */
  invoice?: { id: string; docNumber: string; status: string } | null;
  /** The order behind it (or the order itself) was paid online through FIB. */
  fibPaid?: boolean;
}

export function DocumentDetailPage({
  endpoint,
  ns,
  listPath,
  statusTone,
  postPermission,
  editPath,
  writePermission,
  printPath,
  whatsappDocType,
  dateLabelKey,
  dueDateLabelKey,
  quotation,
}: DocumentDetailPageProps): JSX.Element {
  const tenantName = useAuthStore((s) => s.tenant?.name ?? '');
  const tenantId = useAuthStore((s) => s.tenant?.id ?? null);
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode ?? 'USD');
  const taxOn = moduleEnabled(useEnabledModules(tenantId), 'tax');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Routes pass `documents:salesOrders` (etc) to share one detail page
  // across every doctype. Split off the keyPrefix so `t(...)` resolves
  // root-level keys (`form.*`, `detail.*`, `actions.*` — identical across
  // doctypes) while `tDoc(...)` qualifies the per-doctype subtree
  // (`title`, `status.*`). Without this, `useTranslation('documents:salesOrders')`
  // treats the whole string as a namespace name, no namespace matches, and
  // every `t(...)` call renders the literal key path.
  const [nsName, keyPrefix] = ns.includes(':') ? ns.split(':', 2) : [ns, undefined];
  const { t, i18n } = useTranslation(nsName ?? ns);
  const tDoc = (key: string, defaultValue?: string): string =>
    t(keyPrefix ? `${keyPrefix}.${key}` : key, defaultValue !== undefined ? { defaultValue } : undefined);
  const { t: tc } = useTranslation();
  const canPost = usePermission(postPermission ?? '');
  const canPay = usePermission('payments.write');
  /** A customer's money outside the register (counter staff take it in their shift). */
  const canReceive = usePermission('payments.receive');
  /** Removing a past-draft sales order voids its invoice — the invoice-posting right. */
  const canUnwindOrder = usePermission('invoices.post');
  /** Taking the money AT posting also posts a payment — the third right it needs. */
  const canPostPayment = usePermission('payments.post');
  /** «ترحيل» on an invoice: the paid-or-not question is open. */
  const [askPaid, setAskPaid] = useState(false);
  const canWrite = usePermission(writePermission ?? '');
  const [doc, setDoc] = useState<DocDetail | null>(null);
  const [items, setItems] = useState<Item[] | null>(null);
  const [partyOne, setPartyOne] = useState<Party | null>(null);
  const [posting, setPosting] = useState(false);
  const [warehouseName, setWarehouseName] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [publicLink, setPublicLink] = useState('');
  const [quoteBusy, setQuoteBusy] = useState(false);
  const canConvert = usePermission('invoices.write');
  // Both party routes are gated on `parties.read`; without it the name stays
  // plain text rather than becoming a link to a permission refusal.
  const canReadParties = usePermission('parties.read');
  const canConvertBill = usePermission('bills.write');
  const canWriteQuote = usePermission('quotations.write');
  const canWriteSO = usePermission('sales_orders.write');
  // Opening a line's product needs the catalogue; its photos need attachments.
  const canReadItems = usePermission('items.read');
  const canFiles = usePermission('attachments.read');
  const [detailItem, setDetailItem] = useState<Item | null>(null);

  /**
   * The product behind a line, in the SAME modal the register and the catalogue
   * use — one place that knows how to show an item, rather than a third partial
   * copy of it here.
   *
   * Fetched by id on click, not from the `items` list this page already loads:
   * that list is one page deep, so on a real catalogue the line you clicked is
   * usually not in it. Deliberately not cached — a document is read once and the
   * request is small.
   */
  const openItemDetail = async (itemId: string): Promise<void> => {
    try {
      const r = await api().get<Item>(`/items/${itemId}`);
      setDetailItem(r.data);
    } catch {
      toast.error(tc('errors:server'));
    }
  };

  const reload = async (): Promise<void> => {
    if (!id) return;
    const r = await api().get<DocDetail>(`${endpoint}/${id}`);
    setDoc(r.data);
    // The document's own customer, by id. Fetched here because this is where the
    // party id becomes known, and by id because the /parties LIST returns a
    // single page — on a shop with more customers than that page holds, the
    // header had nothing to render and the whole customer block vanished.
    if (r.data.partyId) {
      void api().get<Party>(`/parties/${r.data.partyId}`)
        .then((pr) => setPartyOne(pr.data))
        .catch(() => { /* non-fatal — the header falls back to the doc's own fields */ });
    }
    // Purchase docs: name the warehouse the goods go into (bills receive on
    // post; a PO carries the intention). Fetched here, not eagerly — most
    // documents have no warehouse concept at all.
    if (r.data.warehouseId) {
      void api().get<{ items: Array<{ id: string; code: string; name: string }> }>('/warehouses')
        .then((w) => {
          const hit = w.data.items.find((x) => x.id === r.data.warehouseId);
          setWarehouseName(hit ? `${hit.code} — ${hit.name}` : null);
        })
        .catch(() => setWarehouseName(null));
    }
  };

  useEffect(() => {
    void reload();
    void api().get<{ items: Item[] }>('/items').then((r) => setItems(r.data.items));
    // No party DIRECTORY here. It only ever served as a fallback for the by-id
    // fetch in reload(), and being one page deep (200 rows) it was empty of
    // exactly the customers that fallback would be needed for — a page of JSON
    // per document view that could not answer the one question it was asked.
    // A shareable public link for the WhatsApp message. Sales orders as well as
    // invoices: both mint the `/d/<token>` paper (documents.routes.ts), and the
    // order is the document a rep or the counter actually hands over. Quotations
    // have no mint route, so asking for one would only 404.
    if ((whatsappDocType === 'invoice' || whatsappDocType === 'salesOrder') && id) {
      void api().get<{ url: string }>(`${endpoint}/${id}/public-link`)
        .then((r) => setPublicLink(r.data.url))
        .catch(() => setPublicLink(''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, endpoint]);

  if (!doc) return <Card><Skeleton className="h-32 w-full" /></Card>;

  const party = partyOne;
  // Charge lines (no item — e.g. the delivery fee) are shown as their own rows in
  // the totals, NOT as product line items. Products stay in the items table.
  const allLines = doc.lines ?? [];
  const productLines = allLines.filter((l) => l.itemId);
  const chargeLines = allLines.filter((l) => !l.itemId);
  const chargesPreTax = chargeLines.reduce((s, l) => s.plus(new Decimal(l.quantity || '0').times(l.unitPrice || '0')), new Decimal(0));
  const productsSubtotal = doc.subtotal !== undefined ? new Decimal(doc.subtotal).minus(chargesPreTax).toFixed() : undefined;
  const dateStr = doc.docDate ?? doc.paymentDate;
  const isPosted = !!doc.postedJournalEntryId;
  const canShowPost =
    postPermission && canPost && !isPosted && (doc.status === 'draft' || doc.status === 'issued' || doc.status === 'approved');
  // Invoices and bills are the two doctypes that post to the ledger, so they're
  // the two whose POSTED documents can still be edited or voided (the server
  // reverses the journal entry — and a bill's received stock — then re-posts).
  // Everything else stays draft-only.
  const revisableKind = endpoint === '/invoices' ? 'invoice' : endpoint === '/bills' ? 'bill' : null;
  // A credit/debit note is itself an undo — it has its own flow (the API refuses
  // it too, this just avoids offering a button that can only fail).
  const isNote = !!doc.isCreditNote || !!doc.isDebitNote;
  const canShowEdit = !!editPath && canWrite && doc.status === 'draft';
  // Posted edit/void. Needs the POST permission as well as write — it reverses a
  // journal entry and posts a new one, which is a ledger write (the server
  // enforces the same pair). Deliberately NOT hidden for docs with payments or a
  // closed period: the server owns those rules and answers with a specific
  // reason, so the user is told WHY rather than left staring at a missing button.
  const canRevisePosted =
    !!revisableKind && canWrite && canPost && !isNote &&
    doc.status !== 'draft' && doc.status !== 'cancelled' &&
    !doc.postedJournalEntryReversed &&
    // Nothing to reverse without a journal entry — treat it as read-only rather
    // than warning about entry "".
    !!doc.postedJournalEntryNumber;
  const canShowPostedEdit = canRevisePosted && !!editPath;
  /**
   * A sales order past draft can be REMOVED, unwinding whatever it caused.
   *
   * It needs the posting permission as well as write, because the cascade
   * reverses journal entries — the same pair the server enforces. Offered even
   * when the server might refuse (a credit note against the invoice, a closed
   * period): the server answers with a specific reason, which tells the shop
   * more than a button that silently is not there. That absence is what made
   * this look impossible rather than merely blocked.
   */
  const canCascadeOrder =
    endpoint === '/sales-orders' && canWrite && canUnwindOrder &&
    doc.status !== 'draft' && doc.status !== 'cancelled';
  const entryNumber = doc.postedJournalEntryNumber ?? '';
  // Base units a bill's posting pushed into stock — what a void gives back.
  const receivedUnits = productLines
    .reduce((s, l) => s.plus(new Decimal(l.quantity || '0').times(l.unitFactor || '1')), new Decimal(0))
    .toFixed();
  // Outstanding = what the customer still owes on this invoice (total − paid).
  // The document status enum has no "partially paid" state, so we derive a clear
  // paid indicator here for display.
  const outstanding = new Decimal(doc.total || '0').minus(doc.amountPaid ?? '0');
  // Paid at the till or the register in another currency's notes.
  const cash = cashNotes(doc);
  const tracksPayment = doc.amountPaid !== undefined;
  const fullyPaid = tracksPayment && Number(doc.total || '0') > 0 && outstanding.lte(0);
  const partlyPaid = Number(doc.amountPaid ?? '0') > 0 && outstanding.gt(0);
  // Record-payment applies to posted receivables (invoices) AND payables (bills).
  // The link targets the customer (AR) or the supplier (AP) account page, where
  // the payment is actually recorded (inbound vs outbound).
  const canRecordPayment = (endpoint === '/invoices' ? canReceive : endpoint === '/bills') && isPosted && canPay && !!doc.partyId && outstanding.gt(0);
  // The party's own page — reached from the Record-payment button and from the
  // party's name in the summary. Empty when the document names nobody, which
  // both call sites already check for before rendering a link.
  const partyHref = doc.partyId
    ? partyPath(endpoint, doc.partyId, (doc as { direction?: string | null }).direction)
    : '';

  const onPost = async (): Promise<void> => {
    if (!doc.id) return;
    // A sales invoice asks whether it was paid (owner, 2026-11-01): an app
    // order paid on delivery used to post as a debt. Only for someone who may
    // take the money, and only when there is something to pay — otherwise the
    // plain confirm, as before.
    if (endpoint === '/invoices' && canPay && canReceive && canPostPayment && doc.partyId && !doc.isCreditNote && outstanding.gt(0)) {
      setAskPaid(true);
      return;
    }
    if (!(await confirmDialog({ message: t('actions.confirmPost', { defaultValue: 'Post this document?' }) }))) return;
    setPosting(true);
    try {
      await api().post(`${endpoint}/${doc.id}/post`, {});
      await reload();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'unprocessable' ? tc('errors:unprocessable') : tc('errors:server'));
    } finally {
      setPosting(false);
    }
  };

  // Order → document conversion, keyed by the source endpoint. Sales orders
  // become draft invoices; purchase orders become draft bills (purchase
  // invoices). Both convert from 'draft' or 'submitted': only the storefront
  // submits an order by itself, and there is no submit action anywhere in this
  // app, so gating on 'submitted' hid the button on every hand-made, till and
  // field order — the one place the office actually needs it. A 'fulfilled'
  // («منجز») order converts only when it has NO invoice (the storefront used to
  // mark orders fulfilled without billing them); the server holds the order
  // locked and refuses a second invoice.
  const convertCfg =
    endpoint === '/sales-orders'
      ? {
          sub: 'invoice', ok: canConvert, navBase: '/sales/invoices', statuses: ['draft', 'submitted'],
          labelKey: 'detail.convertToInvoice', labelDflt: 'Convert to invoice',
          confirmKey: 'detail.confirmConvert', confirmDflt: 'Create an invoice from this order?',
          doneKey: 'detail.convertedToInvoice', doneDflt: 'Invoice created from order.',
        }
      : endpoint === '/purchase-orders'
      ? {
          sub: 'bill', ok: canConvertBill, navBase: '/purchasing/bills', statuses: ['draft', 'submitted'],
          labelKey: 'detail.convertToBill', labelDflt: 'Convert to purchase invoice',
          confirmKey: 'detail.confirmConvertBill', confirmDflt: 'Create a bill (purchase invoice) from this order?',
          doneKey: 'detail.convertedToBill', doneDflt: 'Bill created from order.',
        }
      : null;
  const unbilledFulfilled = endpoint === '/sales-orders' && doc.status === 'fulfilled' && doc.invoice === null;
  const canShowConvert = !!convertCfg && convertCfg.ok && (convertCfg.statuses.includes(doc.status) || unbilledFulfilled);

  const onConvert = async (): Promise<void> => {
    if (!doc.id || !convertCfg) return;
    if (!(await confirmDialog({ message: t(convertCfg.confirmKey, { defaultValue: convertCfg.confirmDflt }) }))) return;
    setConverting(true);
    try {
      const r = await api().post<{ id: string }>(`${endpoint}/${doc.id}/${convertCfg.sub}`, {});
      toast.success(t(convertCfg.doneKey, { defaultValue: convertCfg.doneDflt }));
      navigate(`${convertCfg.navBase}/${r.data.id}`);
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'unprocessable' ? tc('errors:unprocessable') : tc('errors:server'));
      setConverting(false);
    }
  };

  // Quotation workflow (send/accept/reject + convert to invoice/order).
  const quoteStatus = async (action: 'send' | 'accept' | 'reject'): Promise<void> => {
    if (!doc.id) return;
    setQuoteBusy(true);
    try {
      await api().post(`${endpoint}/${doc.id}/${action}`, {});
      await reload();
    } catch { toast.error(tc('errors:server')); } finally { setQuoteBusy(false); }
  };
  const quoteConvert = async (target: 'invoice' | 'order'): Promise<void> => {
    if (!doc.id) return;
    if (!(await confirmDialog({ message: tDoc(target === 'invoice' ? 'confirmConvertInvoice' : 'confirmConvertOrder', target === 'invoice' ? 'Create an invoice from this quote?' : 'Create a sales order from this quote?') }))) return;
    setQuoteBusy(true);
    try {
      const r = await api().post<{ id: string }>(`${endpoint}/${doc.id}/${target}`, {});
      toast.success(tDoc('converted', 'Quote converted.'));
      navigate(target === 'invoice' ? `/sales/invoices/${r.data.id}` : `/sales/orders/${r.data.id}`);
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'unprocessable' ? tc('errors:unprocessable') : tc('errors:server'));
      setQuoteBusy(false);
    }
  };

  /**
   * Delete a draft outright, or VOID a posted document.
   *
   * A void is not a delete: the server posts a reversing journal entry, gives a
   * bill's received goods back to stock, and marks the document cancelled — the
   * row and its ledger history survive. The confirmation spells all of that out,
   * naming the journal entry (and the units) before anything happens.
   */
  const onDelete = async (): Promise<void> => {
    if (!doc.id) return;
    // A converted SALES ORDER is neither a draft nor a revisable document, so it
    // has its own path: the server unwinds everything the order caused and then
    // removes it. The confirmation has to say so plainly — this reverses a
    // customer's payment, and nobody should discover that afterwards.
    const cascading = endpoint === '/sales-orders' && doc.status !== 'draft';
    const voiding = doc.status !== 'draft' && !cascading;
    const message = cascading && doc.status !== 'fulfilled' && !doc.invoice
      // Never billed (an app order still waiting): nothing to reverse.
      ? t('revise.removeOpenOrder', {
          doc: doc.docNumber,
          defaultValue: 'Remove order {{doc}}? It was never turned into an invoice, so no money or stock moves. This cannot be undone.',
        })
      : cascading
      ? t('revise.cancelOrder', {
          doc: doc.docNumber,
          defaultValue: 'This removes {{doc}} and undoes everything it caused: the invoice it became is voided, any payment against it is reversed, and stock it took is put back. The journal entries are reversed, never deleted — the invoice stays visible, marked cancelled.',
        })
      : !voiding
      ? t('actions.confirmDelete', { defaultValue: 'Delete this draft?' })
      : revisableKind === 'bill'
        ? t('revise.voidBill', {
            doc: doc.docNumber, entry: entryNumber, units: receivedUnits,
            defaultValue: 'This will reverse journal entry {{entry}} and return {{units}} units to stock. {{doc}} will be marked cancelled — it stays visible, and its ledger history is kept.',
          })
        : t('revise.voidInvoice', {
            doc: doc.docNumber, entry: entryNumber,
            defaultValue: 'This will reverse journal entry {{entry}} and mark {{doc}} cancelled. The document stays visible, and its ledger history is kept.',
          });
    const confirmed = await confirmDialog({
      message,
      tone: 'danger',
      title: cascading
        ? t('revise.cancelOrderTitle', { defaultValue: 'Remove this order and reverse its money?' })
        : voiding ? t('revise.voidTitle', { defaultValue: 'Void this posted document?' }) : undefined,
      confirmLabel: cascading
        ? t('revise.cancelOrderConfirm', { defaultValue: 'Remove and reverse' })
        : voiding ? t('revise.voidConfirm', { defaultValue: 'Void document' }) : undefined,
    });
    if (!confirmed) return;
    setDeleting(true);
    try {
      await api().delete(`${endpoint}/${doc.id}${cascading ? '?cascade=1' : ''}`);
      if (voiding) {
        toast.success(t('revise.voided', {
          doc: doc.docNumber, entry: entryNumber,
          defaultValue: '{{doc}} was voided — journal entry {{entry}} has been reversed.',
        }));
        // A voided document still exists, so stay on it and show the new state
        // rather than bouncing to the list as a hard delete does.
        await reload();
        setDeleting(false);
        return;
      }
      navigate(listPath);
    } catch (e) {
      toast.error(reviseErrorMessage(e, t, tc));
      setDeleting(false);
    }
  };

  /**
   * Open the edit form for an ALREADY POSTED document — but say plainly first
   * what saving will do to the books, so nobody discovers the reversal after
   * the fact.
   */
  const onEditPosted = async (): Promise<void> => {
    if (!doc.id || !editPath) return;
    const message =
      revisableKind === 'bill'
        ? t('revise.editBill', {
            doc: doc.docNumber, entry: entryNumber, units: receivedUnits,
            defaultValue: 'Saving your changes will reverse journal entry {{entry}}, return {{units}} units to stock, then re-post {{doc}} with the new amounts.',
          })
        : t('revise.editInvoice', {
            doc: doc.docNumber, entry: entryNumber,
            defaultValue: 'Saving your changes will reverse journal entry {{entry}}, then re-post {{doc}} with the new amounts.',
          });
    if (!(await confirmDialog({ message, title: t('revise.editTitle', { defaultValue: 'Edit a posted document?' }) }))) return;
    navigate(`${editPath}/${doc.id}/edit`);
  };

  const fmt = (amt?: string | null): string =>
    formatMoney(amt ?? '0', { locale: i18n.language, currency: doc.currencyCode });

  const itemLabel = (l: DocLine): string => {
    // Prefer the server-joined item name/code (works for any item, not just the
    // first page of /items). Fall back to the loaded list, then to a dash — never
    // the raw UUID (which is what showed for items beyond the 200-item page).
    if (l.itemName) return l.itemCode ? `${l.itemCode} — ${l.itemName}` : l.itemName;
    if (!l.itemId) return '—';
    const it = items?.find((x) => x.id === l.itemId);
    return it ? `${it.code} — ${it.name}` : '—';
  };

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-fg-muted">
            <Link to={listPath} className="underline">{tDoc('title')}</Link>
          </p>
          <h1 className="font-mono text-2xl font-semibold">{doc.docNumber}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge tone={statusTone?.[doc.status] ?? 'neutral'}>
            {tDoc(`status.${doc.status}`, doc.status)}
          </Badge>
          {canShowEdit && (
            <Link to={`${editPath}/${doc.id}/edit`}>
              <Button variant="secondary">
                <Pencil className="h-4 w-4" aria-hidden />
                {tc('actions.edit')}
              </Button>
            </Link>
          )}
          {canShowPostedEdit && (
            <Button variant="secondary" onClick={onEditPosted}>
              <Pencil className="h-4 w-4" aria-hidden />
              {tc('actions.edit')}
            </Button>
          )}
          {(canShowEdit || canRevisePosted || canCascadeOrder) && (
            <Button variant="danger" onClick={onDelete} loading={deleting}>
              <Trash2 className="h-4 w-4" aria-hidden />
              {canRevisePosted ? t('revise.void', { defaultValue: 'Void' }) : tc('actions.delete')}
            </Button>
          )}
          {canShowPost && (
            <Button onClick={onPost} loading={posting}>
              <CheckCircle2 className="h-4 w-4" aria-hidden />
              {tc('actions.post', { defaultValue: 'Post' })}
            </Button>
          )}
          {canRecordPayment && (
            <Link to={partyHref}>
              <Button variant="secondary">
                <Banknote className="h-4 w-4" aria-hidden />
                {t('detail.recordPayment', { defaultValue: 'Record payment' })}
              </Button>
            </Link>
          )}
          {canShowConvert && convertCfg && (
            <Button variant={unbilledFulfilled ? 'primary' : 'secondary'} onClick={onConvert} loading={converting}>
              <Receipt className="h-4 w-4" aria-hidden />
              {t(convertCfg.labelKey, { defaultValue: convertCfg.labelDflt })}
            </Button>
          )}
          {/* The order became an invoice: that is the live document now. */}
          {endpoint === '/sales-orders' && doc.invoice && (
            <Link to={`/sales/invoices/${doc.invoice.id}`}>
              <Button variant="secondary">
                <Receipt className="h-4 w-4" aria-hidden />
                {t('detail.openInvoice', { defaultValue: 'Invoice {{number}}', number: doc.invoice.docNumber })}
              </Button>
            </Link>
          )}
          {quotation && (doc.status === 'draft') && canWriteQuote && (
            <Button variant="secondary" onClick={() => quoteStatus('send')} loading={quoteBusy}>
              {tDoc('send', 'Send')}
            </Button>
          )}
          {quotation && (doc.status === 'draft' || doc.status === 'sent') && canWriteQuote && (
            <>
              <Button onClick={() => quoteStatus('accept')} loading={quoteBusy}>
                <CheckCircle2 className="h-4 w-4" aria-hidden />{tDoc('accept', 'Accept')}
              </Button>
              <Button variant="danger" onClick={() => quoteStatus('reject')} loading={quoteBusy}>{tDoc('reject', 'Reject')}</Button>
            </>
          )}
          {quotation && (doc.status === 'sent' || doc.status === 'accepted') && (
            <>
              {canConvert && (
                <Button variant="secondary" onClick={() => quoteConvert('invoice')} loading={quoteBusy}>
                  <Receipt className="h-4 w-4" aria-hidden />{tDoc('convertToInvoice', 'Convert to invoice')}
                </Button>
              )}
              {canWriteSO && (
                <Button variant="secondary" onClick={() => quoteConvert('order')} loading={quoteBusy}>
                  {tDoc('convertToOrder', 'Convert to order')}
                </Button>
              )}
            </>
          )}
          {/* Cashback is a base-currency wallet; the redeem math (wallet vs
              invoice outstanding) only lines up when the invoice is in the base
              currency — the server rejects otherwise (cashback_base_currency_only). */}
          {endpoint === '/invoices' && isPosted && doc.partyId && doc.currencyCode === baseCurrency && (
            <CashbackRedeemAction
              invoiceId={doc.id}
              partyId={doc.partyId}
              currencyCode={baseCurrency}
              outstanding={new Decimal(doc.total || '0').minus(doc.amountPaid ?? '0').toFixed(4)}
              onDone={reload}
            />
          )}
          {whatsappDocType && party?.phone && (
            <WhatsAppButton
              docType={whatsappDocType}
              phone={party.phone}
              vars={{
                company: tenantName,
                customer: party.displayName,
                docNumber: doc.docNumber,
                date: dateStr ? formatDate(dateStr, { locale: i18n.language }) : '',
                dueDate: doc.dueDate ? formatDate(doc.dueDate, { locale: i18n.language }) : '',
                total: fmt(doc.total),
                paid: fmt(doc.amountPaid),
                balance: fmt(new Decimal(doc.total || '0').minus(doc.amountPaid ?? '0').toFixed(4)),
                currency: doc.currencyCode,
                link: publicLink,
              }}
            />
          )}
          {printPath && (
            // Open the standalone print view in a new tab via window.open rather
            // than an <a target="_blank">: anchors get implicit noopener, so the
            // new tab would NOT inherit the sessionStorage auth and would bounce
            // to login. window.open keeps the opener, so the tab stays signed in.
            <Button
              variant="secondary"
              onClick={() => window.open(appUrl(`${printPath}/${doc.id}/print`), '_blank')}
            >
              <Printer className="h-4 w-4" aria-hidden />
              {tDoc('print', 'Print')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => navigate(listPath)}>
            <DirectionalIcon>
              <ArrowLeft className="h-4 w-4" />
            </DirectionalIcon>
            {tc('actions.back')}
          </Button>
        </div>
      </header>

      <Card>
        <dl className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <Field label={t(dateLabelKey ?? 'detail.date', { defaultValue: 'Date' })}>
            {dateStr ? formatDate(dateStr, { locale: i18n.language }) : '—'}
          </Field>
          {doc.dueDate !== undefined && (
            <Field label={t(dueDateLabelKey ?? 'detail.dueDate', { defaultValue: 'Due date' })}>
              {doc.dueDate ? formatDate(doc.dueDate, { locale: i18n.language }) : '—'}
            </Field>
          )}
          {/* Presence-driven: only bills carry the supplier's own number. */}
          {(doc as { supplierInvoiceNo?: string | null }).supplierInvoiceNo && (
            <Field label={t('detail.supplierInvoiceNo', { defaultValue: 'Supplier invoice no.' })}>
              <span className="font-mono" dir="ltr">{(doc as { supplierInvoiceNo?: string | null }).supplierInvoiceNo}</span>
            </Field>
          )}
          <Field label={t('detail.currency', { defaultValue: 'Currency' })}>
            {doc.currencyCode}
          </Field>
          <Field label={t('detail.party', { defaultValue: 'Party' })}>
            {party && canReadParties && partyHref ? (
              <Link
                to={partyHref}
                className="underline-offset-4 hover:underline hover:text-brand focus:outline-none focus-visible:underline"
              >
                {`${party.code} — ${party.displayName}`}
              </Link>
            ) : party ? `${party.code} — ${party.displayName}` : doc.partyId ?? '—'}
          </Field>
          {(endpoint === '/bills' || endpoint === '/purchase-orders' || endpoint === '/invoices' || endpoint === '/sales-orders') && (
            <Field label={t('detail.warehouse', { defaultValue: 'Warehouse' })}>
              {doc.warehouseId
                ? warehouseName ?? '—'
                // No choice recorded: bills/POs fall to the tenant default;
                // invoices ship by inference from where the stock actually is.
                : (
                  <span className="text-fg-muted">
                    {endpoint === '/invoices' || endpoint === '/sales-orders'
                      ? t('columnsShared.autoWarehouse', { defaultValue: 'Auto' })
                      : t('columnsShared.defaultWarehouse', { defaultValue: 'Default' })}
                  </span>
                )}
            </Field>
          )}
          {doc.postedJournalEntryId && (
            <Field label={t('detail.postedJE', { defaultValue: 'Journal entry' })}>
              <Link to="/accounting/journals" className="font-mono text-xs underline">
                {doc.postedJournalEntryId.slice(0, 8)}…
              </Link>
            </Field>
          )}
          {doc.postedAt && (
            <Field label={t('detail.postedAt', { defaultValue: 'Posted at' })}>
              {formatDate(doc.postedAt, { locale: i18n.language })}
            </Field>
          )}
        </dl>
      </Card>

      {productLines.length > 0 && (
        <Card className="overflow-x-auto p-0">
          <table className="w-full text-start text-sm">
            <thead className="bg-bg-muted text-fg-subtle">
              <tr>
                <th className="px-3 py-2 text-start w-10">#</th>
                <th className="px-3 py-2 text-start">{t('form.line.item')}</th>
                <th className="px-3 py-2 text-start">{t('form.line.unit', { defaultValue: 'Unit' })}</th>
                <th className="px-3 py-2 text-start">{t('form.line.description')}</th>
                <th className="px-3 py-2 text-end">{t('form.line.qty')}</th>
                <th className="px-3 py-2 text-end">{t('form.line.unitPrice')}</th>
                {taxOn && <th className="px-3 py-2 text-end">{t('form.line.taxRate')}</th>}
                <th className="px-3 py-2 text-end">{t('form.line.total')}</th>
              </tr>
            </thead>
            <tbody>
              {productLines.map((l, i) => (
                <tr key={l.id} className="border-t border-border">
                  {/* Position, not the stored lineNo — same reason as the print
                      view: charge lines are shown separately, so quoting the
                      document-wide number leaves gaps in the goods. */}
                  <td className="px-3 py-2 text-fg-muted">{i + 1}</td>
                  <td className="px-3 py-2">
                    {l.itemId && canReadItems ? (
                      <button
                        type="button"
                        onClick={() => void openItemDetail(l.itemId as string)}
                        className="text-start underline-offset-4 hover:underline hover:text-brand focus:outline-none focus-visible:underline"
                        title={t('detail.viewItem', { defaultValue: 'View product details' })}
                      >
                        {itemLabel(l)}
                      </button>
                    ) : itemLabel(l)}
                  </td>
                  {/* The pack size belongs beside the unit: "Carton" does not say
                      how many pieces were bought, and it is the number the
                      quantity is multiplied by to reach stock.
                      A line sold in the base unit records no unit at all, which
                      is why this column was an em dash on three lines in four —
                      the item's own unit of measure is the honest answer there,
                      and it is the fallback the PRINTED copy already uses. */}
                  <td className="px-3 py-2 text-fg-muted">
                    {l.unitName
                      ? `${l.unitName}${Number(l.unitFactor ?? '1') > 1 ? ` ×${trimNum(l.unitFactor ?? '1')}` : ''}`
                      : l.itemUom || '—'}
                  </td>
                  <td className="px-3 py-2">{l.description ?? '—'}</td>
                  <td className="px-3 py-2 text-end font-mono">{trimNum(l.quantity)}</td>
                  <td className="px-3 py-2 text-end font-mono">{fmt(l.unitPrice)}</td>
                  {taxOn && <td className="px-3 py-2 text-end font-mono">{Number(l.taxRate) * 100}%</td>}
                  <td className="px-3 py-2 text-end font-mono">{fmt(l.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Card>
        <table className="w-full text-end text-sm">
          <tbody>
            {doc.subtotal !== undefined && (
              <tr>
                <td className="px-4 py-1 text-fg-muted">{t('form.totals.subtotal')}</td>
                <td className="px-4 py-1 font-mono">{fmt(productsSubtotal)}</td>
              </tr>
            )}
            {chargeLines.map((l) => (
              <tr key={l.id}>
                <td className="px-4 py-1 text-fg-muted">{l.description ?? t('form.totals.delivery', { defaultValue: 'Delivery' })}</td>
                <td className="px-4 py-1 font-mono">{fmt(l.lineTotal)}</td>
              </tr>
            ))}
            {doc.discountTotal !== undefined && Number(doc.discountTotal) > 0 && (
              <tr>
                <td className="px-4 py-1 text-fg-muted">
                  {t('form.totals.discount', { defaultValue: 'Discount' })}
                  {doc.couponCode ? ` (${doc.couponCode})` : ''}
                </td>
                <td className="px-4 py-1 font-mono text-danger">−{fmt(doc.discountTotal)}</td>
              </tr>
            )}
            {Number(doc.taxTotal) > 0 && (
              <tr>
                <td className="px-4 py-1 text-fg-muted">{t('form.totals.tax')}</td>
                <td className="px-4 py-1 font-mono">{fmt(doc.taxTotal)}</td>
              </tr>
            )}
            <tr className="border-t border-border">
              <td className="px-4 py-2 text-base font-semibold">{t('form.totals.total')}</td>
              <td className="px-4 py-2 font-mono text-base font-semibold">{fmt(doc.total)}</td>
            </tr>
            {doc.amountPaid !== undefined && (
              <tr>
                <td className="px-4 py-1 text-fg-muted">
                  {t('detail.amountPaid', { defaultValue: 'Paid' })}
                </td>
                <td className="px-4 py-1 font-mono">{fmt(doc.amountPaid)}</td>
              </tr>
            )}
            {cash && (
              <tr>
                <td className="px-4 py-1 text-fg-muted">
                  {t(`detail.${paidLabelKey(cash.method)}`, { defaultValue: PAID_LABEL_DEFAULT[paidLabelKey(cash.method)] })}
                  {(cash.handed !== null || cash.change) && (
                    <div className="text-[11px] text-fg-subtle">
                      {[
                        cash.handed !== null && t('detail.cashHanded', { defaultValue: 'handed {{amount}}', amount: formatMoney(String(cash.handed), { locale: i18n.language, currency: cash.currency }) }),
                        cash.change && t('detail.cashChange', { defaultValue: 'change {{amount}}', amount: formatMoney(String(cash.change.amount), { locale: i18n.language, currency: cash.change.currency }) }),
                      ].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </td>
                <td className="px-4 py-1 font-mono font-semibold text-success">
                  {formatMoney(String(cash.paid), { locale: i18n.language, currency: cash.currency })}
                </td>
              </tr>
            )}
            {tracksPayment && (isPosted || partlyPaid || fullyPaid) && (
              <tr className="border-t border-border">
                <td className="px-4 py-2 font-semibold">
                  {t('detail.balanceDue', { defaultValue: 'Balance due' })}
                  {partlyPaid && <Badge tone="warning" className="ms-2 align-middle">{t('detail.partiallyPaid', { defaultValue: 'Partially paid' })}</Badge>}
                  {fullyPaid && <Badge tone="success" className="ms-2 align-middle">{t('detail.paidInFull', { defaultValue: 'Paid' })}</Badge>}
                </td>
                <td className={`px-4 py-2 font-mono font-semibold ${outstanding.gt(0) ? 'text-danger' : 'text-success'}`}>{fmt(outstanding.toFixed(4))}</td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>

      {/* What this sale earned. Invoices only — they are the sales the profit
          engine costs, and a credit note is an invoice row wearing
          `isCreditNote`, so it costs as the negative of one. The card carries
          its own `profits.read` gate: without the permission it renders nothing
          and, more to the point, asks the server nothing. */}
      {endpoint === '/invoices' && (
        <SaleProfitCard
          sale={{ docType: doc.isCreditNote ? 'credit_note' : 'invoice', docId: doc.id }}
        />
      )}

      {doc.notes && (
        <Card>
          <h2 className="mb-2 text-sm font-medium text-fg-muted">{t('form.notes')}</h2>
          <p className="whitespace-pre-wrap text-sm">{doc.notes}</p>
        </Card>
      )}

      {endpoint === '/invoices' && (
        <PostInvoiceDialog
          open={askPaid}
          onOpenChange={setAskPaid}
          invoice={{ id: doc.id, docNumber: doc.docNumber, currencyCode: doc.currencyCode, notes: doc.notes, fibPaid: doc.fibPaid }}
          outstanding={outstanding}
          onPosted={reload}
        />
      )}

      {/* Read-only on purpose: no `onAddToCart`, which is what turns the shared
          modal into its catalogue form rather than its register one. */}
      <ItemDetailDialog
        item={detailItem}
        open={detailItem !== null}
        onOpenChange={(o) => { if (!o) setDetailItem(null); }}
        locale={i18n.language}
        // An item with no own currency is priced in the TENANT BASE currency —
        // never this document's (a USD bill must not relabel an IQD price "$").
        currencyFallback={baseCurrency}
        canViewImages={canFiles}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-fg-muted">{label}</dt>
      <dd className="mt-1 text-sm">{children}</dd>
    </div>
  );
}

/** Redeem a customer's cashback against a posted invoice (cashback.write). */
function CashbackRedeemAction({
  invoiceId, partyId, currencyCode, outstanding, onDone,
}: { invoiceId: string; partyId: string; currencyCode: string; outstanding: string; onDone: () => Promise<void> }): JSX.Element | null {
  const { t } = useTranslation('cashback');
  const { t: tc } = useTranslation();
  const { i18n } = useTranslation();
  const canRedeem = usePermission('cashback.write');
  const [balance, setBalance] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void getCashbackBalance(partyId).then((b) => { if (active) setBalance(b); });
    return () => { active = false; };
  }, [partyId]);

  if (!canRedeem || balance === null) return null;
  const avail = new Decimal(balance);
  const due = new Decimal(outstanding);
  if (avail.lte(0) || due.lte(0)) return null;
  const max = Decimal.min(avail, due);

  const submit = async (): Promise<void> => {
    const amt = Decimal.min(new Decimal(amount || '0'), max);
    if (amt.lte(0)) return;
    setBusy(true);
    try {
      await redeemCashback({ invoiceId, amount: amt.toFixed(4) });
      toast.success(t('redeem.success', { defaultValue: 'Cashback redeemed.' }));
      setOpen(false);
      await onDone();
    } catch {
      toast.error(tc('errors:server'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button variant="secondary" onClick={() => { setAmount(max.toFixed(getCurrencyDecimals(currencyCode) ?? 2)); setOpen(true); }}>
        <Gift className="h-4 w-4" aria-hidden />
        {t('redeem.action', { defaultValue: 'Redeem cashback' })}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('redeem.action', { defaultValue: 'Redeem cashback' })}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3 py-2">
            <div className="rounded-md bg-success/10 px-3 py-2 text-sm text-success">
              {t('redeem.available', { defaultValue: 'Available' })}:{' '}
              <span className="font-semibold">{formatMoney(avail.toFixed(getCurrencyDecimals(currencyCode) ?? 2), { locale: i18n.language, currency: currencyCode })}</span>
            </div>
            <label className="text-sm font-medium text-fg">{t('redeem.amount', { defaultValue: 'Amount to redeem' })}</label>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} autoFocus />
            <p className="text-xs text-fg-muted">{t('redeem.max', { defaultValue: 'Up to' })} {formatMoney(max.toFixed(getCurrencyDecimals(currencyCode) ?? 2), { locale: i18n.language, currency: currencyCode })}</p>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)} disabled={busy}>{tc('actions.cancel')}</Button>
            <Button onClick={() => void submit()} loading={busy}>{t('redeem.action', { defaultValue: 'Redeem cashback' })}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
